import styled from 'styled-components'
import { MutableRefObject } from 'react'

import { Typography } from 'components/Typography/Typography'
import { IconInfo } from 'components/Icons/InfoIcon'
import { TokenInput } from './TokenInput'
import { Stylable } from 'utils/types'
import { ReversedWrapper } from 'components/ReversedWrapper/ReversedWrapper'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { Amount } from '@kunalabs-io/kai'

const CollateralTitle = styled(Typography)`
  display: flex;
  align-items: center;
  // margin: 16px 0;
  margin-top: 32px;
  margin-bottom: 12px;
  margin-left: 0;
  margin-right: 0;

  & > div {
    display: flex;
    cursor: pointer;
    & > svg {
      margin-left: 6px;
      path {
        stroke: ${p => p.theme.colors.text.secondary};
      }
    }
  }
`

const Container = styled.div`
  display: flex;
  gap: 16px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    flex-direction: column;
    gap: 32px;
  }
`

const CollateralItem = styled.div<{ disabled?: boolean; $error: boolean }>`
  padding: 16px;
  position: relative;
  display: flex;
  border-radius: 8px;
  border: 0.7px solid ${p => (p.$error ? p.theme.colors.text.danger : p.theme.colors.divider)};

  ${p => p.disabled && `background-color: ${p.theme.colors.background.secondary}`};

  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    width: 50%;
  }
`

interface Props extends Stylable {
  disabled?: boolean
  xIcon?: string
  xSymbol: string
  xError: boolean
  xBalance?: Amount
  handleMaxClickX: () => void
  handleHalfClickX: () => void
  xDepositValue: string
  handleXChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  yIcon?: string
  ySymbol: string
  yBalance?: Amount
  yError: boolean
  handleMaxClickY: () => void
  handleHalfClickY: () => void
  yDepositValue: string
  handleYChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  isReversedPair: boolean
  xCollateralDollars: string
  yCollateralDollars: string
  collateralXRef?: MutableRefObject<HTMLElement | null>
  collateralYRef?: MutableRefObject<HTMLElement | null>
}

export const Collateral = (props: Props) => {
  const {
    xError,
    yError,
    disabled,
    xIcon,
    xSymbol,
    xBalance,
    handleMaxClickX,
    handleHalfClickX,
    xDepositValue,
    handleXChange,
    yIcon,
    ySymbol,
    yBalance,
    handleMaxClickY,
    handleHalfClickY,
    yDepositValue,
    handleYChange,
    style,
    isReversedPair,
    xCollateralDollars,
    yCollateralDollars,
    collateralXRef,
    collateralYRef,
  } = props

  return (
    <div style={style}>
      <CollateralTitle variant="label2" color="secondary" fontWeight="medium">
        Collateral
        <Tooltip
          content="The initial amount of assets you deposit to back your leveraged position."
          position="bottom"
          tooltipBoxStyles={{ marginLeft: 100, marginTop: 0 }}
        >
          <IconInfo width={16} height={16} style={{ marginLeft: 6 }} />
        </Tooltip>
      </CollateralTitle>
      <Container>
        <ReversedWrapper
          childX={
            <CollateralItem disabled={disabled} $error={xError}>
              <TokenInput
                disabled={disabled}
                tokenIcon={xIcon}
                tokenSymbol={xSymbol}
                tokenBalance={xBalance}
                handleMaxClick={handleMaxClickX}
                handleHalfClick={handleHalfClickX}
                value={xDepositValue}
                handleValueChange={handleXChange}
                estimate={xCollateralDollars}
                ref={collateralXRef}
              />
              {xError && (
                <Typography variant="caption" color="danger" style={{ position: 'absolute', bottom: -24, left: 0 }}>
                  {!xDepositValue || Number(xDepositValue) === 0
                    ? `Collateral can't be 0`
                    : `Insufficient ${xSymbol} balance`}
                </Typography>
              )}
            </CollateralItem>
          }
          childY={
            <CollateralItem disabled={disabled} $error={yError}>
              <TokenInput
                disabled={disabled}
                tokenIcon={yIcon}
                tokenSymbol={ySymbol}
                tokenBalance={yBalance}
                handleMaxClick={handleMaxClickY}
                handleHalfClick={handleHalfClickY}
                value={yDepositValue}
                handleValueChange={handleYChange}
                estimate={yCollateralDollars}
                ref={collateralYRef}
              />
              {yError && (
                <Typography variant="caption" color="danger" style={{ position: 'absolute', bottom: -24, left: 0 }}>
                  {!yDepositValue || Number(yDepositValue) === 0
                    ? `Collateral can't be 0`
                    : `Insufficient ${ySymbol} balance`}
                </Typography>
              )}
            </CollateralItem>
          }
          isReversed={isReversedPair}
        />
      </Container>
    </div>
  )
}
